import {
    Headline2,
    BodyRegular,
    LinkWrapper,
    FlexColRg,
    FlexMd,
    FlexSpaceBetween,
    BoldSpan,
    FlexColMd,
} from 'components/Elements';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import LearningObjectivesList from 'pages/learner/components/LearningObjectivesList';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import styled from 'styled-components';
import { breakpoints, sizes } from 'theme';
import strings from '../../strings/strings.json';
import { formatLongDate } from 'lib/time-helpers';
import CourseCompleteDate from 'pages/learner/components/CourseCompleteDate';
import { useContext } from 'react';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import DownloadICSButton from './DownloadICSButton';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { ActivityType } from 'models/Activity';

export default function LearnerCourseDetails(): JSX.Element {
    const { description, learningObjectives, startDate, completedDate, trainerInfo, title, modules } =
        useContext(LearnerCourseContext).learnerCourse!;

    const hasLiveTraining = modules.flatMap((m) => m.activities).some((m) => m.type === ActivityType.LIVE_TRAINING);

    return (
        <Background>
            <Container>
                <DateAndLogoRow>
                    <DateCard>
                        <FlexColMd>
                            <Headline2>{strings.learnerCoursePage.durationTitle}</Headline2>
                            <FlexMd>
                                {completedDate && <CourseCompleteDate completedDate={completedDate} />}
                                <BodyRegular>
                                    <BoldSpan>{strings.learnerCoursePage.start}</BoldSpan> {formatLongDate(startDate)}
                                </BodyRegular>
                            </FlexMd>
                            {hasLiveTraining && (
                                <Notification
                                    notificationType={NotificationType.INFO}
                                    title={strings.learnerCoursePage.calendarMessage.title}
                                    description={strings.learnerCoursePage.calendarMessage.description}
                                    button={<DownloadICSButton courseTitle={title} modules={modules} />}
                                />
                            )}
                        </FlexColMd>
                    </DateCard>

                    {trainerInfo.organisationLogo && (
                        <LinkWrapper href={`/public/organisation/${trainerInfo.organisationPublicName}`}>
                            <LogoGrid>
                                <Image
                                    src={`${DOCUMENTS_URL}/${trainerInfo.organisationLogo}`}
                                    alt={`${trainerInfo.organisationName} logo`}
                                />
                            </LogoGrid>
                        </LinkWrapper>
                    )}
                </DateAndLogoRow>

                <CardContainer>
                    <Headline2>{strings.learnerCoursePage.descriptionTitle}</Headline2>
                    <BodyRegular>{description}</BodyRegular>
                </CardContainer>

                <CardContainer>
                    <Headline2>{strings.learnerCoursePage.learningObjectivesTitle}</Headline2>
                    <LearningObjectivesList learningObjectives={learningObjectives} />
                </CardContainer>
            </Container>
        </Background>
    );
}

const Background = styled.div`
    width: 100%;
`;

const Container = styled(FlexColRg)`
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
    padding: 0 ${sizes.edgePadding};
    margin: 0 auto;

    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
    }
`;

const DateAndLogoRow = styled(FlexSpaceBetween)`
    align-items: center;
    flex-wrap: wrap;
    gap: ${sizes.spacingMd};
`;

const DateCard = styled(LearnerCardBase)`
    display: flex;
    flex-wrap: wrap;
    gap: ${sizes.spacingMd};

    padding: ${sizes.spacingMd};
    width: fit-content;
    align-items: center;
`;

const LogoGrid = styled(LearnerCardBase)`
    display: flex;
    align-items: center;
    height: min-content;
    width: min-content;
    padding: ${sizes.spacingSm};
    @media (max-width: ${breakpoints.sm}) {
        margin-top: unset;
    }
`;

const Image = styled.img`
    border-radius: ${sizes.borderRadiusMd};
    max-width: 244px;
    max-height: 88px;
    width: auto;
    height: auto;
    object-fit: contain;
`;

const CardContainer = styled(LearnerCardBase)`
    width: 100%;
    padding: ${sizes.spacingMd};
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingSm};
`;
